import { formatTimeOfDay } from "../utils/format";
import { SUGGESTION_COPY } from "./copy";
import type { SuggestionDraft, SuggestionDraftStorage, SuggestionRequest, SuggestionTextIssue } from "./types";

export const SUGGESTION_MIN_LENGTH = 10;
export const SUGGESTION_MAX_LENGTH = 4000;
export const SUGGESTION_DRAFT_KEY_PREFIX = "speak.suggest-change.draft:";
export const SUGGESTION_SURFACE_MAX_LENGTH = 200;
const VAGUE_MAX_WORDS = 3;
const CHAT_TOKEN = "{{chat}}";

/** Why the text cannot be sent yet, or null when it can. Length is counted after trimming. */
export function suggestionTextIssue(text: string): SuggestionTextIssue | null {
  const length = text.trim().length;
  if (length < SUGGESTION_MIN_LENGTH) return "too_short";
  if (length > SUGGESTION_MAX_LENGTH) return "too_long";
  return null;
}

/** True for typed text too short to act on, such as "make it better", so the hint can ask for more. */
export function isVagueSuggestion(text: string): boolean {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= VAGUE_MAX_WORDS;
}

export function buildSuggestionRequest(draft: SuggestionDraft): SuggestionRequest {
  return {
    type: "improvement",
    trigger: "manual",
    surface: (draft.surface.trim() || "unknown").slice(0, SUGGESTION_SURFACE_MAX_LENGTH),
    text: draft.text.trim(),
    context: draft.context,
  };
}

/** Replaces each `{{key}}` in a copy string. */
export function fillSuggestionCopy(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((text, [key, value]) => text.split(`{{${key}}}`).join(value), template);
}

/** The chat line around its "Chat with us" link: the text before it and after it. */
export function splitChatLine(line: string): [string, string] {
  const at = line.indexOf(CHAT_TOKEN);
  if (at < 0) return [line, ""];
  return [line.slice(0, at), line.slice(at + CHAT_TOKEN.length)];
}

/** The local time of day the next suggestion is allowed, such as "3:40 pm". */
export function formatNextAllowedTime(nextAllowedAt: string, locale?: string): string {
  return formatTimeOfDay(Date.parse(nextAllowedAt), locale);
}

/** The account's saved draft, or "" when signed out, when there is none, or when storage is unavailable (reading `localStorage` itself can throw). */
export function loadSuggestionDraft(storage: () => SuggestionDraftStorage, accountKey: string | undefined): string {
  if (!accountKey) return "";
  try {
    return storage().getItem(`${SUGGESTION_DRAFT_KEY_PREFIX}${accountKey}`) ?? "";
  } catch {
    return "";
  }
}

/** Saves the draft under the signed in account only, removing it when empty; storage errors are ignored because the draft is a convenience. */
export function saveSuggestionDraft(storage: () => SuggestionDraftStorage, accountKey: string | undefined, text: string): void {
  if (!accountKey) return;
  const key = `${SUGGESTION_DRAFT_KEY_PREFIX}${accountKey}`;
  try {
    if (text) storage().setItem(key, text);
    else storage().removeItem(key);
  } catch {
    // A private window or blocked storage only loses the draft.
  }
}

/** A random index into `SUGGESTION_COPY.fieldLabels`, picked each time the box opens. */
export function pickFieldLabelIndex(random: () => number = Math.random): number {
  return Math.min(Math.floor(random() * SUGGESTION_COPY.fieldLabels.length), SUGGESTION_COPY.fieldLabels.length - 1);
}
